import { Alert, AlertIcon, Box, Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  BidStatus,
  BidStatusTransitionTypeDto,
  SourcingEventStatus,
  useGetSourcingEventQuery,
  type BuyerBidDetailDto,
} from "../../../autogen/bff-api";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";
import { latestTransition } from "./bidActivity";
import type { BidEvaluationSummary } from "./bidEvaluationSummary";
import { getBidMinimumRequirementProgress } from "./bidMinimumRequirements";
import { BidQualificationDecision } from "./BidQualificationDecision";
import { RejectBidButton } from "./BidRejectionActionCard";

interface Props {
  detail: BuyerBidDetailDto;
  summary: BidEvaluationSummary;
  awarded: boolean;
  onMarkWinner: () => void;
  canSelectWinner: boolean;
}

export const BidActionsCard = ({
  detail,
  summary,
  awarded,
  onMarkWinner,
  canSelectWinner,
}: Props) => {
  if (detail.bid.status === BidStatus.Rejected) {
    return null;
  }
  if (detail.bid.status === BidStatus.Awarded) {
    return <AwardedBanner detail={detail} />;
  }
  if (awarded) {
    return <NotSelectedBanner />;
  }
  return (
    <ActiveActions
      detail={detail}
      summary={summary}
      onMarkWinner={onMarkWinner}
      canSelectWinner={canSelectWinner}
    />
  );
};

const NotSelectedBanner = () => {
  const { t } = useTranslation();
  return (
    <Box
      bg="smBackgroundSecondary"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Stack spacing="0.5">
        <Text fontWeight="semibold" color="smText">
          {t("Not selected")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("This bid was not selected for award, but it was not rejected.")}
        </Text>
      </Stack>
    </Box>
  );
};

const AwardedBanner = ({ detail }: { detail: BuyerBidDetailDto }) => {
  const { t } = useTranslation();
  const displayDateTime = useDisplayDateWithTime();
  const awardedAt = latestTransition(
    detail.bid.statusHistory,
    BidStatusTransitionTypeDto.Awarded,
  )?.at;
  return (
    <Box
      bg="green.50"
      border="1px solid"
      borderColor="green.200"
      rounded="xl"
      px="5"
      py="4"
      color="green.800"
    >
      <Stack spacing="0.5">
        <Text fontWeight="semibold">{t("Selected as winner")}</Text>
        {awardedAt && (
          <Text fontSize="sm" opacity={0.85}>
            {displayDateTime(awardedAt)}
          </Text>
        )}
      </Stack>
    </Box>
  );
};

const CONCLUDED_STATUSES: SourcingEventStatus[] = [
  SourcingEventStatus.Awarded,
  SourcingEventStatus.Closed,
  SourcingEventStatus.Completed,
  SourcingEventStatus.Cancelled,
];

const ActiveActions = ({
  detail,
  summary,
  onMarkWinner,
  canSelectWinner,
}: {
  detail: BuyerBidDetailDto;
  summary: BidEvaluationSummary;
  onMarkWinner: () => void;
  canSelectWinner: boolean;
}) => {
  const { t } = useTranslation();
  const { data: event } = useGetSourcingEventQuery({ eventId: detail.bid.forEvent });
  // The V1→V2 migration awards at the event level and leaves the winning bid SUBMITTED.
  const concluded = event != null && CONCLUDED_STATUSES.includes(event.status);
  const showRejectButton = detail.bid.status === BidStatus.Submitted && !concluded;
  // The button only navigates to the award tab, which refuses to select a winner before the
  // evaluation is completed — offering it earlier would point at a screen that will not act on it.
  const showWinnerButton = canSelectWinner && summary.state === "evaluated" && summary.rank === 1;
  const minimumRequirements = getBidMinimumRequirementProgress(detail);
  if (!showRejectButton && !showWinnerButton) {
    return null;
  }

  return (
    <Box
      bg="smBackground"
      border="1px solid"
      borderColor="smBorder"
      rounded="xl"
      px="5"
      py="4"
    >
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="wider"
        textTransform="uppercase"
        color="smMuted"
        mb="3"
      >
        {t("Actions")}
      </Text>
      <Stack spacing="3">
        {/* Renders itself only for procedures that qualify at the tender stage. */}
        <BidQualificationDecision detail={detail} />
        {showRejectButton && <RejectBidButton detail={detail} />}
        {showWinnerButton && minimumRequirements.notFulfilled > 0 && (
          <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
            <AlertIcon />
            {t(
              "A tender that does not meet an absolute minimum requirement must be rejected, not awarded. Review the minimum requirements before you award.",
            )}
          </Alert>
        )}
        {showWinnerButton && minimumRequirements.notFulfilled === 0 && minimumRequirements.pending > 0 && (
          <Alert status="warning" rounded="md" fontSize="sm" alignItems="flex-start">
            <AlertIcon />
            {t(
              "You do not yet know whether the tender meets the minimum requirements. Assess them before you award.",
            )}
          </Alert>
        )}
        {showWinnerButton && (
          <Button colorScheme="teal" onClick={onMarkWinner} width="full">
            {t("Mark as winner")}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
